import { EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  useToast,
  Flex,
  TableContainer,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
  Tooltip,
  Input,
} from "@chakra-ui/react";
import { useHasPermission } from "App/DMRC/components/common/PermissionsFunction/permissiosnFunction";
import CustomInput from "components/common/customInput/CustomInput";
import { Paginationlimit } from "components/common/functions/variable";
import Pagination from "components/common/Pagination/Pagination";
import TableLoader from "components/common/TableLoader/TableLoader";
import React, { useCallback, useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import { HiOutlineFilter, HiOutlineRefresh } from "react-icons/hi";
import { appRequest } from "routes";

interface Stations {
  station_name: string;
  aliases: string[];
}

type props = {
  cityId: string;
  cityCode: string;
};

export default function Stations({ cityId, cityCode }: props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [stations, setStations] = useState<Stations[]>([]);
  const [debouncedStationID, setDebouncedStationID] = useState<string>("");
  const [stationid, setStationid] = useState<string>("");
  const [editStations, setEditStations] = useState<{ [key: string]: string[] }>(
    {}
  );
  const toast = useToast();

  const getStations = useCallback(
    async (currentP?: number) => {
      try {
        setLoading(true);
        let page = currentP ? currentP : currentPage;
        let limit = Paginationlimit;
        //  const id = "670e3fa30afc7035c8fecaba";
        const id = cityId;
        let search = debouncedStationID || "";
        const request: any = {
          id,
          limit,
          page,
          search,
          cityCode
        };

        const response: any = await appRequest("metro", "allStations", request);

        if (response.status === "success") {
          setStations(response.stations || []);
          setTotalPages(response?.totalPages || 0);
        }
      } catch (error: any) {
        console.log("Error fetching stations:", error);
      } finally {
        setLoading(false);
      }
    },
    [Paginationlimit, currentPage, debouncedStationID] // Dependencies
  );

  const handlePageChange = (page: any) => {
    setCurrentPage(page.selected + 1);
  };

  const handleRefreshAllData = () => {
    getStations();
  };

  const handlestationChange = (e: any) => {
    setStationid(e.target.value.trim());
  };

  const handleEdit = (stationName: string) => {
    setEditStations((prev) => {
      const updated = { ...prev };
      delete updated[stationName];
      return updated;
    });
  };

  //  update the aliases
  const handleFieldChange = (stationName: string, value: string) => {
    setEditStations((prev) => ({
      ...prev,
      [stationName]: value.split(",").map((alias) => alias.trim()),
    }));
  };

  const UpdateAliases = async (station_name: string) => {
    try {
      setLoading(true);

      // const id = "670e3fa30afc7035c8fecaba";
      const id = cityId;
      const request = {
        id,
        cityCode,
        station_name,
        aliases: editStations[station_name],
      };
      const response: any = await appRequest(
        "metro",
        "updateStationAliases",
        request
      );
      if (response.status === "success") {
        toast({
          title: "Success",
          description: "aliases updated successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setStations((prev) =>
          prev.map((station) =>
            station.station_name === station_name
              ? { ...station, aliases: request.aliases }
              : station
          )
        );
        handleEdit(station_name);
      }
    } catch (error: any) {
      console.error("Error updating aliases :", error);
      toast({
        title: "Error",
        description: "Failed to update aliases.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStations();
  }, [currentPage, debouncedStationID]);

  // Debounce effect for station
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedStationID(stationid);
    }, 3000);

    return () => {
      clearTimeout(handler);
    };
  }, [stationid]);

  const canEditStations = useHasPermission("canEditStations");

  return (
    <>
      <Box
        p={5}
        rounded={"lg"}
        bg={"white"}
        shadow={"rgb(0 0 0 / 20%) 0px 0px 11px"}
      >
        <Flex justify={"end"} gap={2} mb={2}>
          <Tooltip label="Refresh" hasArrow>
            <IconButton
              aria-label="Refresh"
              icon={<HiOutlineRefresh />}
              colorScheme="teal"
              variant="outline"
              borderWidth={2}
              _hover={{ bg: "teal.100" }}
              onClick={handleRefreshAllData}
              size={{ base: "sm", md: "md" }}
            />
          </Tooltip>
          <Popover>
            <PopoverTrigger>
              <IconButton
                aria-label="Filter"
                colorScheme="teal"
                icon={<HiOutlineFilter />}
                size={{ base: "sm", md: "md" }}
              />
            </PopoverTrigger>
            <Portal>
              <PopoverContent shadow={"lg"} borderWidth={2}>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody p={5}>
                  <VStack spacing={6} mt={2}>
                    <CustomInput
                      name={"Filter By Station Name"}
                      label={"Filter By Station Name"}
                      onChange={handlestationChange}
                    />
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        </Flex>
        <TableContainer className="customScrollBar" pb={2}>
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>Station Name</Th>
                <Th>Aliases</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <TableLoader
              noDataText="No Stations  Available"
              show={stations?.length}
              loader={loading}
            >
              <Tbody>
                {stations?.map((item: any, index: number) => (
                  <Tr>
                    <Td>
                      <Text fontWeight={500}>
                        {item?.station_name || "N/A"}
                      </Text>
                    </Td>
                    <Td>
                      {editStations[item?.station_name] ? (
                        // <Text>
                        //   {editStations[item?.station_name].join(", ")}
                        // </Text>

                        <Input
                          value={
                            editStations[item?.station_name]?.join(", ") || ""
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              item?.station_name,
                              e.target.value
                            )
                          }
                          size="sm"
                        />
                      ) : (
                        <Text>{item?.aliases.join(", ") || "N/A"}</Text>
                      )}
                    </Td>
                    <Td>
                      {editStations[item?.station_name] !== undefined ? (
                        <Flex gap="2">
                          <IconButton
                            aria-label="Save"
                            icon={<FaSave />}
                            colorScheme="teal"
                            size="sm"
                            variant={"outline"}
                            mr={2}
                            onClick={() => UpdateAliases(item?.station_name)}
                          />
                          <IconButton
                            aria-label="Cancel"
                            icon={<FaTimes />}
                            colorScheme="red"
                            size="sm"
                            variant={"outline"}
                            onClick={() => handleEdit(item?.station_name)}
                          />
                        </Flex>
                      ) : (
                        <>
                          {canEditStations && (
                            <IconButton
                              aria-label="edit"
                              variant={"outline"}
                              colorScheme="teal"
                              icon={<EditIcon />}
                              size="sm"
                              onClick={() =>
                                setEditStations((prev) => ({
                                  ...prev,
                                  [item?.station_name]: [...item?.aliases],
                                }))
                              }
                            />
                          )}
                        </>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </TableLoader>
          </Table>
        </TableContainer>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </Box>
    </>
  );
}
